import React, { useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronRight, Building2 } from 'lucide-react';

/**
 * Extracted from CategoryConsole.js (component-split refactor, 2026-10-09).
 * Parses the AI strategy response for the "Potential Alternative Suppliers"
 * section and renders it as a collapsible list. Returns null when nothing
 * is parsed, so the hosting page never shows an empty panel.
 */
export function AlternativeSuppliers({ strategy }) {
  const parseSuppliers = (text) => {
    if (!text) return [];
    const lines = text.split('\n');
    const suppliers = [];
    let inSection = false;
    let current = null;
    for (const line of lines) {
      if (line.toLowerCase().includes('**potential alternative supplier')) { inSection = true; continue; }
      if (inSection && line.startsWith('**') && !line.toLowerCase().includes('potential') && !line.toLowerCase().includes('name') && !line.toLowerCase().includes('special') && !line.toLowerCase().includes('geography') && !line.toLowerCase().includes('why')) break;
      if (!inSection) continue;
      const trimmed = line.replace(/^[-*]\s*/, '').trim();
      if (trimmed.startsWith('**Name**')) {
        if (current) suppliers.push(current);
        current = { name: trimmed.replace('**Name**:', '').replace('**Name**', '').trim() };
      } else if (current && trimmed.startsWith('**Speciality**')) {
        current.speciality = trimmed.replace('**Speciality**:', '').replace('**Speciality**', '').trim();
      } else if (current && trimmed.startsWith('**Geography**')) {
        current.geography = trimmed.replace('**Geography**:', '').replace('**Geography**', '').trim();
      } else if (current && trimmed.startsWith('**Why Consider**')) {
        current.why = trimmed.replace('**Why Consider**:', '').replace('**Why Consider**', '').trim();
      }
    }
    if (current) suppliers.push(current);
    return suppliers;
  };

  const suppliers = parseSuppliers(strategy.strategy);
  const [suppOpen, setSuppOpen] = useState(false);

  if (suppliers.length === 0) return null;

  return (
    <Collapsible open={suppOpen} onOpenChange={setSuppOpen} data-testid="alternative-suppliers">
      <CollapsibleTrigger asChild>
        <button className="flex items-center justify-between w-full text-left px-3 py-2 rounded-md bg-muted/50 hover:bg-muted transition-colors text-sm" data-testid="suppliers-toggle">
          <div className="flex items-center gap-2 font-medium">
            <ChevronRight className={`w-4 h-4 transition-transform ${suppOpen ? 'rotate-90' : ''}`} />
            <Building2 className="w-4 h-4 text-muted-foreground" />
            Potential Alternative Suppliers
            <span className="text-xs font-normal text-muted-foreground">(not in current spend data)</span>
          </div>
          <span className="text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded-full">AI-suggested — not verified</span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-3 rounded-lg border border-border p-4 divide-y divide-border/60">
          {suppliers.map((s, i) => (
            <div key={i} className="py-3 first:pt-0 last:pb-0 grid grid-cols-[1fr_auto] gap-x-6 items-start" data-testid={`suggested-supplier-${i}`}>
              <div className="space-y-1">
                <p className="font-semibold text-sm">{s.name}</p>
                {s.why && <p className="text-xs text-muted-foreground">{s.why}</p>}
              </div>
              <div className="text-right space-y-0.5 shrink-0">
                {s.speciality && <p className="text-xs"><span className="text-muted-foreground">Speciality: </span>{s.speciality}</p>}
                {s.geography && <p className="text-xs"><span className="text-muted-foreground">Geography: </span>{s.geography}</p>}
              </div>
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
